class MultishipShippingForm extends BaseMultishipForm {
  constructor() {
    super();
    this.startShipDate = null;
    this.endShipDate = null;
    this.minDefaultDaysOut = 0;
    this.minShippingDateOut = this.getAttribute('data-min-shipping-date-out');
    this.datePicker = null;
    this.blackoutDates = [];
    this.datePickerInitialized = false;
    this.errorMessage = null;
  }

  determineInitialState() {
    this.loadShippingData();
    if (!this.isAddressComplete() && !this.shippingData.shippingMethodSku) return 'address_required';
    if (this.isAddressComplete() && !this.shippingData.shippingMethodSku) return 'unfilled';
    return this.shippingData.shippingMethodSku ? 'filled' : 'form';
  }

  getDefaultState() {
    return 'address_required';
  }

  loadShippingData() {
    this.shippingData = {
      shippingMethodSku: this.data.shippingMethodSku || '',
      shippingMethod: this.data.shippingMethod || '',
      shippingPrice: this.data._shippingPrice || '',  // Use underscore version
      requestedShipDate: this.data.requestedShipDate || '',
      approximateArrivalDate: this.data.approximateArrivalDate || ''
    };
    this.addressData = {
      firstName: this.data.firstName || '',
      lastName: this.data.lastName || '',
      address1: this.data.address1 || '',
      city: this.data.city || '',
      provinceCode: this.data.provinceCode || '',
      zip: this.data.zip || '',
      countryCode: this.data.countryCode || ''
    };
  }

  isAddressComplete() {
    return this.addressData &&
      this.addressData.firstName &&
      this.addressData.lastName &&
      this.addressData.address1 &&
      this.addressData.city &&
      this.addressData.provinceCode &&
      this.addressData.zip &&
      this.addressData.countryCode;
  }

  getTemplate() {
    switch (this.state) {
      case 'unfilled':
        return this.getUnfilledTemplate();
      case 'address_required':
        return this.getAddressRequiredTemplate();
      case 'form':
        return this.getFormTemplate();
      case 'filled':
        return this.getFilledTemplate();
      case 'error':
        return this.getErrorTemplate();
      default:
        console.error('Invalid state:', this.state);
        return '';
    }
  }

  getUnfilledTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <button type="button" class="multiship__button" data-element="show-form-btn" aria-label="Select shipping method and ship date">
        ${this.iconCirclePlus()}
        Select Shipping Method and Ship Date
      </button>
    `;
  }

  getAddressRequiredTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section multiship__button multiship__button--disabled" aria-live="polite">
        ${this.iconCirclePlus("gray")}
        <div>
          <strong>Add Shipping Information (required):</strong> Please complete recipient & address first. 
        </div>
      </div>
    `;
  }

  getFormTemplate() {
    const showShipDatePicker = this.dataset.showShipDatePicker !== "false";

    let shipDateHTML = '';
    if (showShipDatePicker) {
      shipDateHTML = `
        <div class="ship-date floating-label form-field">
          <label for="ship-date-${this.lineItemId}">Ship Date</label>
          <input type="text" data-element="ship-date" id="ship-date-${this.lineItemId}" readonly aria-required="true">
          <div data-error="ship-date" aria-live="polite" role="alert"></div>
        </div>
      `;
    }

    const defaultHelperText = 'All products are handmade and shipped from our facility in Washington state. Please select an APPROXIMATE Ship Date and Shipping Method below, and an approximate Arrival Date will be shown at the bottom. PLEASE ALLOW UP TO 3 BUSINESS DAYS before and after the selected Ship Date for your order to be prepared and shipped.';
    const defaultHelperTextNoDate = 'All products are handmade and shipped from our facility in Washington state. Please select a Shipping Method below.';

    const helperText = showShipDatePicker
      ? (this.dataset.helperText || defaultHelperText)
      : (this.dataset.helperTextNoDate || defaultHelperTextNoDate);

    return `
    ${this.baseMultishipFormStyles()}
    <div class="multiship__section">
      <div class="multiship-form" data-form-id="${this.lineItemId}" role="form" aria-labelledby="shipping-form-heading-${this.lineItemId}">
        <div class="multiship__header">
          <h4 id="shipping-form-heading-${this.lineItemId}">Shipping Information</h4>
        </div>
        <div>
          <p class="helper" style="margin-top: 2rem;">
          ${helperText}
          </p>
        </div>
        <div class="multiship-form__inputs">
          <div class="shipping-method floating-label form-field">
            <label for="shipping-method-${this.lineItemId}">Shipping Method</label>
            <select data-element="shipping-method" id="shipping-method-${this.lineItemId}" aria-required="true">
              <option value="">Select a Shipping Method:</option>
            </select>
            <div data-error="shipping-method" aria-live="polite" role="alert"></div>
          </div>
          ${shipDateHTML}
        </div>
        <div data-element="arrival-date" aria-live="polite">
          ${this.generateShippingInfoHTML()}
        </div>
        <div class="multiship__actions">
          <button type="button" data-element="save-shipping-btn" class="button">Save <span class="sr-only">Shipping Options</span></button>
          <button type="button" data-element="cancel-shipping-btn" class="link-button">Cancel</button>
        </div>
      </div>
    </div>
  `;
  }

  getFilledTemplate() {
    const data = this.shippingData;
    const formattedShipDate = this.formatDateToMonthDayYear(data.requestedShipDate);
    const showEstimatedDeliveryDate = this.dataset.showEstimatedDeliveryDate ? this.dataset.showEstimatedDeliveryDate : "true";
    const showShipDatePicker = this.dataset.showShipDatePicker !== "false";

    let estimatedDeliveryDate = "";
    if (showEstimatedDeliveryDate == "true") {
      estimatedDeliveryDate = `
        <dt>Estimated Arrival</dt>
        <dd>${data.approximateArrivalDate}</dd>
      `;
    }

    let shipDateDisplay = "";
    if (showShipDatePicker) {
      shipDateDisplay = `
        <dt>Approximate Ship Date</dt>
        <dd>${formattedShipDate}</dd>
      `;
    }

    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section">
        <div class="multiship__header">
          <h4 id="shipping-filled-heading-${this.lineItemId}">Shipping Information</h4>
          <button type="button" class="link-button" data-element="edit-shipping-btn" aria-label="Edit shipping information">Edit</button>
        </div>
        <dl class="multiship-dl" aria-labelledby="shipping-filled-heading-${this.lineItemId}">
          <dt>Shipping Method</dt>
          <dd>${data.shippingMethod}</dd>
          ${shipDateDisplay}
          ${estimatedDeliveryDate}
        </dl>
      </div>
    `;
  }

  getErrorTemplate() {
    const defaultMessage = "We're having trouble loading the available shipping methods. This could be due to a temporary connection issue. Please try again shortly.";
    const displayMessage = this.errorMessage || defaultMessage;
    
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section multiship__section--error" role="alert">
        <div class="multiship__header">
          <h4>Unable to Load Shipping Options</h4>
        </div>
        <p>${displayMessage}</p>
        <p>You can also call us at <a href="tel:18884260781" class="link-button">1-888-426-0781</a> to place your order.</p>
        <div class="multiship__actions">
          <button type="button" class="button" data-element="retry-shipping-btn">
            Try Again
          </button>
        </div>
      </div>
    `;
  }

  formatDateToMonthDayYear(dateString) {
    const date = new Date(dateString + 'T00:00:00Z');
    const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
    return date.toLocaleDateString('en-US', options);
  }

  attachEventListeners() {
    if (this.state === 'unfilled') {
      const showFormBtn = this.shadowRoot.querySelector(`[data-element="show-form-btn"]`);
      if (showFormBtn) {
        showFormBtn.addEventListener('click', () => this.showForm());
      }
    } else if (this.state === 'form') {
      const saveBtn = this.shadowRoot.querySelector(`[data-element="save-shipping-btn"]`);
      const cancelBtn = this.shadowRoot.querySelector(`[data-element="cancel-shipping-btn"]`);
      const shippingMethodSelect = this.shadowRoot.querySelector(`[data-element="shipping-method"]`);

      if (saveBtn) {
        saveBtn.addEventListener('click', () => this.handleSubmit());
      }
      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => this.cancelForm());
      }
      if (shippingMethodSelect) {
        shippingMethodSelect.addEventListener('change', () => this.updateArrivalDate());
      }

      // Ensure proper tab order
      this.ensureTabOrder();
    } else if (this.state === 'filled') {
      const editBtn = this.shadowRoot.querySelector(`[data-element="edit-shipping-btn"]`);
      if (editBtn) {
        editBtn.addEventListener('click', () => this.showForm());
      }
    } else if (this.state === 'error') {
      const retryBtn = this.shadowRoot.querySelector('[data-element="retry-shipping-btn"]');
      if (retryBtn) {
        retryBtn.addEventListener('click', async () => {
          try {
            this.errorMessage = null;
            this.state = 'form';
            this.render();
            
            await new Promise(resolve => requestAnimationFrame(resolve));
            
            await this.loadShippingOptions();
            
            this.datePickerInitialized = false;
            this.initializeDatePicker();
            
            if (this.shippingData) {
              this.populateFormWithExistingData();
            }
          } catch (error) {
            console.error('Error retrying shipping options load:', error);
            this.state = 'error';
            this.render();
          }
        });
      }
    }
  }

  ensureTabOrder() {
    const form = this.shadowRoot.querySelector('.multiship-form');
    const focusableElements = form.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    focusableElements.forEach((el, index) => {
      el.setAttribute('tabindex', index + 1);
    });
  }

  cancelForm() {
    if (this.isAddressComplete()) {
      this.state = 'filled';
    } else {
      this.state = 'unfilled';
    }
    this.render();
  }

  determineMinMaxDates(dateRangeJsonString) {
    if (!dateRangeJsonString) return;
    const dateRangeJson = JSON.parse(dateRangeJsonString);
    const currentYear = new Date().getFullYear();
    
    // Parse the month and day
    const [startMonth, startDay] = dateRangeJson.start.split('-').map(num => parseInt(num, 10));
    const [endMonth, endDay] = dateRangeJson.end.split('-').map(num => parseInt(num, 10));
    
    // Create dates with proper padding
    this.startShipDate = `${currentYear}-${startMonth.toString().padStart(2, '0')}-${startDay.toString().padStart(2, '0')}`;
    this.endShipDate = `${currentYear}-${endMonth.toString().padStart(2, '0')}-${endDay.toString().padStart(2, '0')}`;
    
    // Adjust start date if needed
    this.startShipDate = this.adjustStartShipDate(this.startShipDate);
  }

  adjustStartShipDate(startShipDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDaysOut = this.minShippingDateOut ? parseInt(this.minShippingDateOut) : this.minDefaultDaysOut;
    
    const startDate = new Date(startShipDate);
    startDate.setHours(0, 0, 0, 0);
    
    if (startDate <= today) {
      const adjustedDate = new Date(today);
      adjustedDate.setDate(today.getDate() + minDaysOut);
      return adjustedDate.toISOString().split('T')[0];
    }
    return startShipDate;
  }

  initializeDatePicker() {
    if (this.datePickerInitialized) return;

    const showShipDatePicker = this.dataset.showShipDatePicker !== "false";

    // When ship date picker is hidden, default to today's date
    if (!showShipDatePicker) {
      const today = new Date().toISOString().split('T')[0];
      if (!this.shippingData) this.shippingData = {};
      this.shippingData.requestedShipDate = today;
      this.datePickerInitialized = true;
      return;
    }

    this.shipDateRange = this.getAttribute('data-item-shipping-range');
    this.blackoutDates = JSON.parse(this.getAttribute('data-item-blackout-dates') || '[]');
    this.determineMinMaxDates(this.shipDateRange);

    const shipDateInput = this.shadowRoot.querySelector(`[data-element="ship-date"]`);
    if (shipDateInput && typeof flatpickr !== 'undefined') {
      const initialViewDate = this.findFirstAvailableDate();

      this.datePicker = flatpickr(shipDateInput, {
        dateFormat: "Y-m-d",
        minDate: this.startShipDate,
        maxDate: this.endShipDate,
        disable: [
          (date) => {
            return this.isBlackoutDate(date) || 
                   this.isThisAWeekendDay(date) || 
                   !this.isNotEarlierThanToday(date);
          }
        ],
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            this.updateArrivalDate();
          }
        },
        onReady: (selectedDates, dateStr, instance) => {
          instance.jumpToDate(initialViewDate);
        },
        onOpen: () => {
          shipDateInput.setAttribute('aria-expanded', 'true');
        },
        onClose: () => {
          shipDateInput.setAttribute('aria-expanded', 'false');
        }
      });

      // Add ARIA attributes
      shipDateInput.setAttribute('role', 'combobox');
      shipDateInput.setAttribute('aria-autocomplete', 'none');
      shipDateInput.setAttribute('aria-expanded', 'false');
      shipDateInput.setAttribute('aria-haspopup', 'dialog');

      // Hide datepicker when input loses focus
      shipDateInput.addEventListener('blur', () => {
        setTimeout(() => {
          if (!this.datePicker.isOpen && document.activeElement !== shipDateInput) {
            this.datePicker.close();
          }
        }, 100);
      });

      // Show datepicker when input gains focus
      shipDateInput.addEventListener('focus', () => {
        this.datePicker.open();
      });

      // Close datepicker when focus moves outside the form
      this.shadowRoot.addEventListener('focusin', (event) => {
        if (event.target !== shipDateInput && this.datePicker.isOpen) {
          this.datePicker.close();
        }
      });

      this.datePickerInitialized = true;

      if (this.shippingData && this.shippingData.requestedShipDate) {
        this.datePicker.setDate(this.shippingData.requestedShipDate);
      } else {
        this.datePicker.clear();
      }
    } else {
      console.error('Ship date input not found or flatpickr is not available');
    }
  }

  isNotEarlierThanToday(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }

  isThisADayBefore(date) {
    const startDate = new Date(this.startShipDate);
    return date <= startDate;
  }

  isThisADayAfter(date) {
    const maxDate = new Date(this.endShipDate);
    return date >= maxDate;
  }

  isBlackoutDate(date) {
    return this.blackoutDates.includes(flatpickr.formatDate(date, "Y-m-d"));
  }

  isThisAWeekendDay(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  findFirstAvailableDate() {
    const startDate = new Date(this.startShipDate);
    const endDate = new Date(this.endShipDate);
    let currentDate = new Date(startDate);

    // Iterate through dates from start to end to find first available
    while (currentDate <= endDate) {
      if (!this.isBlackoutDate(currentDate) &&
          !this.isThisAWeekendDay(currentDate) &&
          this.isNotEarlierThanToday(currentDate)) {
        return currentDate;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Fallback to startShipDate if no available date found
    return startDate;
  }

  updateDatePickerConstraints(minDate, maxDate, blackoutDates) {
    this.minShipDate = new Date(minDate);
    this.maxShipDate = new Date(maxDate);
    this.blackoutDates = blackoutDates;

    if (this.datePicker) {
      this.datePicker.set('minDate', this.minShipDate);
      this.datePicker.set('maxDate', this.maxShipDate);
      this.datePicker.set('disable', [
        (date) => {
          return this.isBlackoutDate(date) || 
                 this.isThisAWeekendDay(date) || 
                 !this.isNotEarlierThanToday(date);
        }
      ]);
      this.datePicker.redraw();
    }
  }

  render() {
    super.render();
  }

  async showForm() {
    super.showForm();
    this.errorMessage = null;
    this.state = 'form';
    this.render();
    
    try {
      await this.loadShippingOptions();
      
      this.initializeDatePicker();
      
      if (this.shippingData) {
        this.populateFormWithExistingData();
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      this.state = 'error';
      this.render();
    }
  }

  populateFormWithExistingData() {
    const shippingMethodSelect = this.shadowRoot.querySelector(`[data-element="shipping-method"]`);
    const shipDateInput = this.shadowRoot.querySelector(`[data-element="ship-date"]`);
    const arrivalDateElement = this.shadowRoot.querySelector(`[data-element="arrival-date"]`);

    if (this.shippingData.shippingMethodSku) {
      shippingMethodSelect.value = this.shippingData.shippingMethodSku;
    }
    if (this.shippingData.requestedShipDate && shipDateInput) {
      shipDateInput.value = this.shippingData.requestedShipDate;
    }
    if (this.shippingData.approximateArrivalDate) {
      arrivalDateElement.textContent = `Estimated arrival: ${this.shippingData.approximateArrivalDate}`;
    }
    // Only update arrival date if we don't have existing data
    if (!this.shippingData.approximateArrivalDate) {
      this.updateArrivalDate();
    }
  }

  async loadShippingOptions() {
    try {
      const itemSku = this.getAttribute('data-line-sku') || '';
      const url = `${this.apiBaseUrl}/shipping_rates/available?country=${this.addressData.countryCode}&state=${this.addressData.provinceCode}&sku=${encodeURIComponent(itemSku)}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.restricted) {
        this.errorMessage = data.message || 'Shipping is not available for this item to the selected state.';
        throw new Error(this.errorMessage);
      }
      
      if (!data.rates || data.rates.length === 0) {
        this.errorMessage = 'No shipping rates available';
        throw new Error(this.errorMessage);
      }

      this.populateShippingOptions(data.rates);
      
      // If we successfully loaded rates, make sure we're not in error state
      if (this.state === 'error') {
        this.state = 'form';
        this.render();
      }

    } catch (error) {
      console.error('Error loading shipping options:', error);
      this.state = 'error';
      this.render();
    }
  }

  populateShippingOptions(rates) {
    const select = this.shadowRoot.querySelector(`[data-element="shipping-method"]`);
    select.innerHTML = `
      <option value="">Select a Shipping Method:</option>
      ${rates.map(rate => `
        <option value="${this.escapeHtml(rate.shipping_service.service_code)}" data-price="${rate.price}">
          ${this.escapeHtml(rate.shipping_service.service_name)} - $${(rate.price / 100).toFixed(2)}
        </option>
      `).join('')}
    `;
    if (this.shippingData && this.shippingData.shippingMethodSku) {
      select.value = this.shippingData.shippingMethodSku;
    } else {
      select.value = ""; // Set to empty option if no shipping method is selected
    }
    this.updateArrivalDate();
  }

  generateShippingInfoHTML(shippingMethodText = '', arrivalDate = '') {
    const showEstimatedDeliveryDate = this.dataset.showEstimatedDeliveryDate ? this.dataset.showEstimatedDeliveryDate : "true";
    let estimatedDeliveryDate = ``;
    if (showEstimatedDeliveryDate == "true") {
      estimatedDeliveryDate = `<div>
        <dt>Approximate Arrival Date:</dt>
        <dd>${this.escapeHtml(arrivalDate)}</dd>
      </div>`
    }
    return `
    <dl class="multiship-dl">
      <div>
        <dt>Shipping Type:</dt>
        <dd>${this.escapeHtml(shippingMethodText)}</dd>
      </div>
      ${estimatedDeliveryDate}
    </dl>
    <p class="helper" style="margin-bottom: 2rem;">* All Ship Dates are approximate. Shipping fees are non-refundable once items have shipped.</p>
  `;
  }

  async updateArrivalDate() {
    const showShipDatePicker = this.dataset.showShipDatePicker !== "false";
    const shipDateInput = this.shadowRoot.querySelector(`[data-element="ship-date"]`);
    const shippingMethodSelect = this.shadowRoot.querySelector(`[data-element="shipping-method"]`);
    // Use picker value if shown, otherwise use default date from shippingData
    const shipDate = showShipDatePicker && shipDateInput ? shipDateInput.value : this.shippingData?.requestedShipDate;
    const shippingMethod = shippingMethodSelect.value;

    let shippingMethodText = '';
    let arrivalDate = '';

    if (shippingMethod) {
      shippingMethodText = shippingMethodSelect.selectedOptions[0].text.split(' - ')[0];
    }

    if (shipDate && shippingMethod) {
      try {
        const url = `${this.apiBaseUrl}/shipping_rates/earliest_arrival_date?sku=${shippingMethod}&ship_on=${shipDate}&country=${this.addressData.countryCode}&state=${this.addressData.provinceCode}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const responseText = await response.text();

        try {
          const jsonResponse = JSON.parse(responseText);
          arrivalDate = jsonResponse.date;
        } catch (jsonError) {
          const dateMatch = responseText.match(/Approximate Arrival Date:.*?<span.*?>(.*?)<\/span>/s);
          arrivalDate = dateMatch ? dateMatch[1].trim() : '';
        }

        this.arrivalDate = arrivalDate;
      } catch (error) {
        console.error('Error updating arrival date:', error);
        arrivalDate = 'Unable to calculate arrival date';
        this.arrivalDate = arrivalDate;
      }
    }

    const arrivalDateElement = this.shadowRoot.querySelector(`[data-element="arrival-date"]`);
    arrivalDateElement.innerHTML = this.generateShippingInfoHTML(shippingMethodText, arrivalDate);
  }

  showError(field, message) {
    const errorElement = this.shadowRoot.querySelector(`[data-error="${field}"]`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('error-message');
      errorElement.setAttribute('role', 'alert');
    }
  }

  clearErrors() {
    const errorElements = this.shadowRoot.querySelectorAll('[data-error]');
    errorElements.forEach(element => {
      element.textContent = '';
      element.classList.remove('error-message');
      element.removeAttribute('role');
    });
  }

  validateForm() {
    this.clearErrors();
    let isValid = true;
    const showShipDatePicker = this.dataset.showShipDatePicker !== "false";

    const shippingMethodSelect = this.shadowRoot.querySelector(`[data-element="shipping-method"]`);
    const shipDateInput = this.shadowRoot.querySelector(`[data-element="ship-date"]`);

    if (!shippingMethodSelect.value) {
      this.showError('shipping-method', 'Please select a shipping method.');
      isValid = false;
    }

    // Only validate ship date if the picker is shown
    if (showShipDatePicker && shipDateInput && !shipDateInput.value) {
      this.showError('ship-date', 'Please select a ship date.');
      isValid = false;
    }

    return isValid;
  }

  async handleSubmit() {
    if (!this.validateForm()) {
      return;
    }

    await super.handleSubmit();
    const form = this.shadowRoot.querySelector(`[data-form-id="${this.lineItemId}"]`);
    if (!form) {
      console.error('Form element not found');
      return;
    }

    const showShipDatePicker = this.dataset.showShipDatePicker !== "false";
    const shippingMethodSelect = form.querySelector(`[data-element="shipping-method"]`);
    const shipDateInput = form.querySelector(`[data-element="ship-date"]`);
    const arrivalDateElement = form.querySelector(`[data-element="arrival-date"]`);

    const shippingMethod = shippingMethodSelect.value;
    const shippingPrice = shippingMethodSelect.selectedOptions[0].dataset.price;
    // Use picker value if shown, otherwise use default date from shippingData
    const shipDate = showShipDatePicker && shipDateInput ? shipDateInput.value : this.shippingData?.requestedShipDate;
    const approximateArrivalDate = this.arrivalDate;

    if (!shippingMethod || !shipDate || !approximateArrivalDate) {
      console.error('Missing required shipping information');
      return;
    }

    try {
      const updatedData = {
        ...this.data,
        shippingMethodSku: shippingMethod,
        shippingMethod: shippingMethodSelect.selectedOptions[0].text.split(' - ')[0],
        _shippingPrice: shippingPrice,  // Store with underscore to keep hidden
        requestedShipDate: shipDate,
        approximateArrivalDate: approximateArrivalDate
      };

      this.saveDataProperty(updatedData);
      this.updateProperty('Shipping method', updatedData.shippingMethod);
      this.updateProperty('_shippingPrice', String(shippingPrice));
      this.updateProperty('Ship on', this.formatDate(new Date(updatedData.requestedShipDate)));
      
      const result = await this.updateCart({});

      this.shippingData = {
        shippingMethodSku: updatedData.shippingMethodSku,
        shippingMethod: updatedData.shippingMethod,
        shippingPrice: updatedData._shippingPrice,
        requestedShipDate: updatedData.requestedShipDate,
        approximateArrivalDate: updatedData.approximateArrivalDate
      };
      this.state = 'filled';

      this.render();

    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    });
  }
}

customElements.define('multiship-shipping-form', MultishipShippingForm);
